import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useAdminAuth } from './AdminAuth';
import { apiError } from './adminApi';
import { useSeo } from '@/lib/seo';

export default function AdminLogin() {
  const { user, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useSeo({ title: 'Admin sign in | Amden Group', robots: 'noindex, nofollow' });

  useEffect(() => {
    if (user) navigate('/admin', { replace: true });
  }, [user, navigate]);

  if (user) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await login(email.trim(), password);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(apiError(err, 'Incorrect email or password'));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-900 px-4" data-testid="admin-login">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-lift">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">Amden Group</p>
        <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-navy-900">Administration</h1>
        <p className="mt-2 text-sm text-slate-500">Sign in to manage website content and bookings.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="admin-email" className="text-sm font-bold text-navy-900">Email</label>
            <input
              id="admin-email" type="email" autoComplete="username" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email"
              className="mt-1.5 h-11 w-full rounded-md border border-slate-300 px-3.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="text-sm font-bold text-navy-900">Password</label>
            <input
              id="admin-password" type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password"
              className="mt-1.5 h-11 w-full rounded-md border border-slate-300 px-3.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
            />
          </div>
          {error && (
            <p role="alert" data-testid="admin-login-error" className="inline-flex items-center gap-2 text-sm font-semibold text-red-700">
              <AlertCircle className="h-4 w-4" aria-hidden="true" /> {error}
            </p>
          )}
          <button
            type="submit" disabled={busy}
            data-testid="admin-login-submit"
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-navy-900 text-sm font-bold text-white transition-colors hover:bg-navy-700 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {busy ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
